import { PlayerProfile } from '../types';

export const STORAGE_KEY_PROFILES = 'kabyleGame_playerProfiles_v2';
export const STORAGE_KEY_ACTIVE_ID = 'kabyleGame_activePlayerId_v2';

export const ADMIN_PLAYER_ID = 'player_admin_special';
export const ADMIN_PASSWORD = 'AmawalAksil2026';

export const ADMIN_PROFILE: PlayerProfile = {
  id: ADMIN_PLAYER_ID,
  name: 'Admin',
  score: 25000,
  unlockedLevels: [0, 1, 2, 3, 4],
  unlockedSkins: ['default', 'uccen', 'teryel', 'ayyis', 'igider'],
  currentSkin: 'default',
  seenWords: [],
  lastSaved: Date.now(),
  createdAt: 0,
  difficulty: 'DEBUTANT',
  isAdmin: true
};

export function normalizePlayerId(name: string): string {
  const trimmed = (name || '').trim();
  if (trimmed.toLowerCase() === 'admin' || trimmed === ADMIN_PLAYER_ID) {
    return ADMIN_PLAYER_ID;
  }
  let raw = trimmed.toLowerCase();
  while (raw.startsWith('player_')) {
    raw = raw.slice(7);
  }
  const clean = raw.replace(/[^a-z0-9_]/gi, '_');
  return `player_${clean || 'voyageur'}`;
}

export function getAllProfiles(): Record<string, PlayerProfile> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PROFILES);
    let profiles: Record<string, PlayerProfile> = {};

    if (raw) {
      const parsed = JSON.parse(raw);
      Object.keys(parsed).forEach((key) => {
        const p = parsed[key];
        if (p && p.name && p.name.startsWith('player_')) {
          let stripped = p.name;
          while (stripped.startsWith('player_')) {
            stripped = stripped.slice(7);
          }
          p.name = stripped ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : 'Voyageur';
        }
        profiles[key] = p;
      });
    } else {
      // Migrate legacy localStorage if available
      const legacyName = localStorage.getItem('kabyleGame_playerName') || '';
      const legacyLevels = localStorage.getItem('kabyleGame_unlockedLevels');
      const legacyScore = localStorage.getItem('kabyleGame_score');
      const legacySkins = localStorage.getItem('kabyleGame_skins');
      const legacyCurrentSkin = localStorage.getItem('kabyleGame_currentSkin');
      const legacySeenWords = localStorage.getItem('kabyleGame_seenWords');

      if (legacyName && legacyName !== 'Admin') {
        const id = normalizePlayerId(legacyName);
        profiles[id] = {
          id,
          name: legacyName,
          score: legacyScore ? parseInt(legacyScore, 10) || 0 : 0,
          unlockedLevels: legacyLevels ? JSON.parse(legacyLevels) : [0],
          unlockedSkins: legacySkins ? JSON.parse(legacySkins) : ['default'],
          currentSkin: legacyCurrentSkin || 'default',
          seenWords: legacySeenWords ? JSON.parse(legacySeenWords) : [],
          lastSaved: Date.now(),
          createdAt: Date.now(),
          difficulty: 'DEBUTANT',
          isAdmin: false
        };
      }
    }

    // Always ensure the special Admin profile is present, intact, and at least Amɣar Azemni (25000 XP)
    const currentAdminScore = profiles[ADMIN_PLAYER_ID]?.score;
    const adminScore = (currentAdminScore === 9999 || !currentAdminScore) 
      ? 25000 
      : Math.max(25000, currentAdminScore);

    profiles[ADMIN_PLAYER_ID] = {
      ...ADMIN_PROFILE,
      ...(profiles[ADMIN_PLAYER_ID] || {}),
      id: ADMIN_PLAYER_ID,
      name: 'Admin',
      score: adminScore,
      isAdmin: true,
      unlockedLevels: [0, 1, 2, 3, 4],
      unlockedSkins: ['default', 'uccen', 'teryel', 'ayyis', 'igider']
    };

    return profiles;
  } catch (e) {
    console.error('Error reading player profiles', e);
    return { [ADMIN_PLAYER_ID]: ADMIN_PROFILE };
  }
}

export function saveAllProfiles(profiles: Record<string, PlayerProfile>): void {
  try {
    // Keep Admin protected in storage
    const currentAdminScore = profiles[ADMIN_PLAYER_ID]?.score;
    const adminScore = (currentAdminScore === 9999 || !currentAdminScore) 
      ? 25000 
      : Math.max(25000, currentAdminScore);

    profiles[ADMIN_PLAYER_ID] = {
      ...ADMIN_PROFILE,
      ...(profiles[ADMIN_PLAYER_ID] || {}),
      id: ADMIN_PLAYER_ID,
      name: 'Admin',
      score: adminScore,
      isAdmin: true,
      unlockedLevels: [0, 1, 2, 3, 4],
      unlockedSkins: ['default', 'uccen', 'teryel', 'ayyis', 'igider']
    };
    localStorage.setItem(STORAGE_KEY_PROFILES, JSON.stringify(profiles));
  } catch (e) {
    console.error('Error saving player profiles', e);
  }
}

export function getActivePlayerId(): string {
  try {
    const active = localStorage.getItem(STORAGE_KEY_ACTIVE_ID);
    if (active) return active;
    
    // Check legacy player name
    const legacyName = localStorage.getItem('kabyleGame_playerName');
    if (legacyName) {
      const id = normalizePlayerId(legacyName);
      localStorage.setItem(STORAGE_KEY_ACTIVE_ID, id);
      return id;
    }
  } catch (e) {
    console.error('Error getting active player id', e);
  }
  return normalizePlayerId('Voyageur');
}

export function setActivePlayerId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY_ACTIVE_ID, id);
  } catch (e) {
    console.error('Error setting active player id', e);
  }
}

export function getActivePlayerProfile(): PlayerProfile {
  try {
    const activeId = getActivePlayerId();
    const profiles = getAllProfiles();
    if (profiles[activeId]) {
      return profiles[activeId];
    }
    const match = Object.values(profiles).find(
      p => p.id === activeId || normalizePlayerId(p.name) === activeId
    );
    if (match) return match;
  } catch (e) {
    console.error('Error getting active profile', e);
  }
  return getOrCreatePlayerProfile('Voyageur');
}

export function getOrCreatePlayerProfile(name: string): PlayerProfile {
  const id = normalizePlayerId(name);
  const profiles = getAllProfiles();

  if (id === ADMIN_PLAYER_ID || name.trim().toLowerCase() === 'admin') {
    return profiles[ADMIN_PLAYER_ID];
  }

  if (profiles[id]) {
    return profiles[id];
  }

  // Derive human-readable display name without 'player_'
  let displayName = name.trim();
  while (displayName.toLowerCase().startsWith('player_')) {
    displayName = displayName.slice(7).trim();
  }
  if (!displayName) {
    displayName = 'Voyageur';
  } else if (displayName.toLowerCase() !== 'voyageur') {
    displayName = displayName.charAt(0).toUpperCase() + displayName.slice(1);
  }

  // Create new profile
  const newProfile: PlayerProfile = {
    id,
    name: displayName,
    score: 0,
    unlockedLevels: [0],
    unlockedSkins: ['default'],
    currentSkin: 'default',
    seenWords: [],
    lastSaved: Date.now(),
    createdAt: Date.now(),
    difficulty: 'DEBUTANT',
    isAdmin: false
  };

  profiles[id] = newProfile;
  saveAllProfiles(profiles);
  return newProfile;
}

export function saveLevelProgress(
  playerId: string,
  data: Partial<Omit<PlayerProfile, 'id' | 'isAdmin'>>
): PlayerProfile | null {
  return autoSavePlayerData(playerId, data);
}

export function autoSavePlayerData(
  playerId: string,
  data: Partial<Omit<PlayerProfile, 'id' | 'isAdmin'>>
): PlayerProfile | null {
  if (!playerId) return null;

  const profiles = getAllProfiles();

  // Admin profile is special and protected
  if (playerId === ADMIN_PLAYER_ID || data.name === 'Admin') {
    profiles[ADMIN_PLAYER_ID] = {
      ...ADMIN_PROFILE,
      ...(profiles[ADMIN_PLAYER_ID] || {}),
      ...data,
      id: ADMIN_PLAYER_ID,
      name: 'Admin',
      isAdmin: true,
      unlockedLevels: [0, 1, 2, 3, 4],
      unlockedSkins: ['default', 'uccen', 'teryel', 'ayyis', 'igider'],
      lastSaved: Date.now()
    };
    saveAllProfiles(profiles);
    return profiles[ADMIN_PLAYER_ID];
  }

  let profile = profiles[playerId];
  if (!profile) {
    profile = {
      id: playerId,
      name: data.name || 'Voyageur',
      score: 0,
      unlockedLevels: [0],
      unlockedSkins: ['default'],
      currentSkin: 'default',
      seenWords: [],
      lastSaved: Date.now(),
      createdAt: Date.now(),
      difficulty: 'DEBUTANT',
      isAdmin: false
    };
  }

  const updated: PlayerProfile = {
    ...profile,
    ...data,
    id: playerId,
    isAdmin: false,
    lastSaved: Date.now()
  };

  profiles[playerId] = updated;
  saveAllProfiles(profiles);
  return updated;
}

/**
 * Resets all regular players' progress while keeping Admin 100% intact & protected.
 */
export function resetAllRegularPlayerSaves(): { resetCount: number; remainingProfiles: PlayerProfile[] } {
  const profiles = getAllProfiles();
  let resetCount = 0;
  const newProfiles: Record<string, PlayerProfile> = {};

  // Admin remains 100% untouched
  newProfiles[ADMIN_PLAYER_ID] = {
    ...ADMIN_PROFILE,
    unlockedLevels: [0, 1, 2, 3, 4],
    unlockedSkins: ['default', 'uccen', 'teryel', 'ayyis', 'igider']
  };

  // For regular players, we count them as reset
  Object.keys(profiles).forEach((key) => {
    if (key !== ADMIN_PLAYER_ID && !profiles[key].isAdmin) {
      resetCount++;
    }
  });

  saveAllProfiles(newProfiles);

  return {
    resetCount,
    remainingProfiles: Object.values(newProfiles)
  };
}

/**
 * Resets or deletes a single regular player. Refuses to reset or delete Admin.
 */
export function resetSinglePlayerSave(playerId: string): boolean {
  if (playerId === ADMIN_PLAYER_ID) {
    return false; // Protected!
  }

  const profiles = getAllProfiles();
  if (!profiles[playerId]) return false;

  profiles[playerId] = {
    ...profiles[playerId],
    score: 0,
    unlockedLevels: [0],
    unlockedSkins: ['default'],
    currentSkin: 'default',
    seenWords: [],
    lastSaved: Date.now()
  };

  saveAllProfiles(profiles);
  return true;
}

export function deleteSinglePlayerProfile(playerId: string): boolean {
  if (playerId === ADMIN_PLAYER_ID) {
    return false; // Protected!
  }

  const profiles = getAllProfiles();
  if (profiles[playerId]) {
    delete profiles[playerId];
    saveAllProfiles(profiles);
    return true;
  }
  return false;
}

/**
 * Checks if a saved player profile with game progress exists (case-insensitive).
 */
export function hasPlayerSaveData(profile: PlayerProfile): boolean {
  if (!profile) return false;
  if (profile.isAdmin) return true;
  return (
    (profile.unlockedLevels && profile.unlockedLevels.length > 1) ||
    profile.score > 0 ||
    (profile.seenWords && profile.seenWords.length > 0) ||
    (profile.unlockedSkins && profile.unlockedSkins.length > 1)
  );
}

/**
 * Find existing profile case-insensitively.
 */
export function findExistingProfileByName(name: string): PlayerProfile | null {
  const cleanInput = name.trim().toLowerCase();
  if (!cleanInput) return null;
  const profiles = getAllProfiles();
  const id = normalizePlayerId(name);
  if (profiles[id]) return profiles[id];

  // Case-insensitive name lookup
  const match = Object.values(profiles).find(
    p => p.name.trim().toLowerCase() === cleanInput
  );
  return match || null;
}
